import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import EditViewModal from "./components/modal/EditViewModal";
import ResourceSidebar from "./components/ResourceSidebar";
import VideoWall from "./components/VideoWall";
import { LAYOUT_TYPES } from "./const";
import { mapDeviceStreamFields, resolveStreamConfig } from "./lib/streamConfig";
import useDataSourceManager from "hooks/useDataSourceManager";
import useAuthScope from "hooks/useAuthScope";
import { useAuth } from "contexts/AuthContext";
import { useStartViewingCommand } from "./hooks/useStartViewingCommand";
// Architecture overview: see README.md in this folder

const DEFAULT_LAYOUT = "4x4";
const SAVE_VIEW_TIMEOUT_MS = 15000;

/** JSON-RPC errors arrive as `{ code, message }` with no `data`. */
const getResponseError = (response) => {
  if (!response) return "No response from server.";
  if (response.data !== undefined) return null;
  if (response.code !== undefined || response.message) {
    return response.message || `Request failed (${response.code}).`;
  }
  return null;
};

/**
 * Map site devices API item → sidebar row.
 * API shape (info):
 *   macAddress, deviceName, deviceType ("ipcam"|"nvr"),
 *   deviceParentId (NVR MAC when ipcam is under NVR),
 *   linkStatus ("online"|"offline"), favorite (true|false|null)
 */
const mapDeviceToSidebarItem = (item) => {
  const info = item?.info || {};
  const streamFields = mapDeviceStreamFields(info);
  return {
    id: info.macAddress || info.id || "",
    name: info.deviceName || info.macAddress || "",
    siteId: info.siteId || info.siteID || info.site_id || info.site?.id || "",
    favorited: info.favorite === true,
    deviceType: info.deviceType,
    deviceParentId: info.deviceParentId || null,
    linkStatus: info.linkStatus || "offline",
    ...streamFields,
  };
};

/** Split site devices: standalone ipcam → Device; nvr + child ipcam via deviceParentId → NVR */
const partitionSurveillanceDevices = (items) => {
  const mapped = items
    .map(mapDeviceToSidebarItem)
    .filter((d) => d.id);

  const nvrList = mapped
    .filter((d) => d.deviceType === "nvr")
    .map((nvr) => ({ ...nvr, cameras: [] }));

  const nvrById = Object.fromEntries(nvrList.map((nvr) => [nvr.id, nvr]));
  const standaloneCameras = [];

  mapped.forEach((device) => {
    if (device.deviceType === "nvr") return;
    if (device.deviceType !== "ipcam" && device.deviceType) return;

    const parentNvr = device.deviceParentId && nvrById[device.deviceParentId];
    if (parentNvr) {
      parentNvr.cameras.push(device);
    } else {
      standaloneCameras.push(device);
    }
  });

  return { devices: standaloneCameras, nvrs: nvrList };
};

const SurveillanceLiveview = () => {
  const [views, setViews] = useState([]);
  const [devices, setDevices] = useState([]);
  const [nvrs, setNvrs] = useState([]);
  const [editingView, setEditingView] = useState(null);
  // Recorded detail data per view id — fetched via `liveViews/{liveViewId}.read`
  // and reused when the user presses ▶ (play) or edits/updates a view.
  const [viewDetailsMap, setViewDetailsMap] = useState({});
  const [activeView, setActiveView] = useState(null);

  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const { auth, standalone, isAuthReady } = useAuthScope();
  const { token: authToken } = useAuth();

  const realmId = auth.realm?.id || "";
  const orgId = auth.orgs?.id || "";
  const siteId = auth.sites?.id || "";

  const entId = standalone === false ? realmId : null;
  const resolvedOrgId = standalone ? realmId : orgId;

  const requestStartViewing = useStartViewingCommand({
    standalone,
    entId,
    orgId: resolvedOrgId,
    siteId,
    isAuthReady,
  });

  // Every list read gets its own uuid; only the latest one is applied so a slow,
  // older response can't overwrite a list that already includes a newly saved view.
  const latestListUuidRef = useRef(null);
  const staleListUuidsRef = useRef(new Set());
  const devicesUuid = useMemo(() => uuidv4(), []);
  // Maps in-flight detail/update request uuid → { viewId, intent: "fetch" | "update" }
  const detailRequestMapRef = useRef({});
  // Maps in-flight create request uuid → submitted view payload
  const createRequestMapRef = useRef({});
  // Maps in-flight create/update request uuid → { resolve, timeoutId } for the Save View modal
  const pendingSaveRef = useRef({});

  const trackSave = useCallback(
    (requestUuid) =>
      new Promise((resolve) => {
        const timeoutId = window.setTimeout(() => {
          delete pendingSaveRef.current[requestUuid];
          resolve({ ok: false, message: "Request timed out. Please try again." });
        }, SAVE_VIEW_TIMEOUT_MS);
        pendingSaveRef.current[requestUuid] = { resolve, timeoutId };
      }),
    []
  );

  const settleSave = useCallback((requestUuid, result) => {
    const pending = pendingSaveRef.current[requestUuid];
    if (!pending) return;
    window.clearTimeout(pending.timeoutId);
    delete pendingSaveRef.current[requestUuid];
    pending.resolve(result);
  }, []);

  useEffect(() => () => {
    Object.values(pendingSaveRef.current).forEach(({ timeoutId }) => window.clearTimeout(timeoutId));
    pendingSaveRef.current = {};
  }, []);

  // Flat lookup of camera id -> name, combining standalone devices and
  // NVR-attached cameras, so each slot's deviceId can be resolved to a name.
  const cameraNameById = useMemo(() => {
    const map = {};
    devices.forEach((device) => {
      map[device.id] = device.name;
    });
    nvrs.forEach((nvr) => {
      nvr.cameras?.forEach((camera) => {
        map[camera.id] = camera.name;
      });
    });
    return map;
  }, [devices, nvrs]);

  const findDeviceById = useCallback(
    (deviceId) =>
      devices.find((d) => d.id === deviceId) ||
      nvrs.flatMap((n) => n.cameras || []).find((c) => c.id === deviceId),
    [devices, nvrs]
  );

  const resolveStreamConfigForDevice = useCallback(
    (deviceId) => {
      const device = findDeviceById(deviceId);
      return resolveStreamConfig({ device, authToken });
    },
    [findDeviceById, authToken]
  );

  // Fetch live views list on mount / scope change
  const fetchLiveViews = () => {
    if (!isAuthReady || !siteId) return;

    const apiKey = standalone
      ? "/orgs/:orgId/sites/:siteId/settings/liveViews.read"
      : "/ents/:entId/orgs/:orgId/sites/:siteId/settings/liveViews.read";
    const restfulParams = standalone
      ? { orgId: resolvedOrgId, siteId }
      : { entId, orgId: resolvedOrgId, siteId };

    if (latestListUuidRef.current) staleListUuidsRef.current.add(latestListUuidRef.current);
    const requestUuid = uuidv4();
    latestListUuidRef.current = requestUuid;
    sendRequest(apiKey, requestUuid, restfulParams);
  };

  // Site surveillance devices (cameras + NVRs) for the sidebar Device / NVR lists
  const fetchDevices = () => {
    if (!isAuthReady || !resolvedOrgId) return;

    const apiKey = siteId
      ? standalone
        ? "/orgs/:orgId/sites/:siteId/devices.read"
        : "/ents/:entId/orgs/:orgId/sites/:siteId/devices.read"
      : standalone
        ? "/orgs/:orgId/devices.read"
        : "/ents/:entId/orgs/:orgId/devices.read";
    const restfulParams = siteId
      ? standalone
        ? { orgId: resolvedOrgId, siteId }
        : { entId, orgId: resolvedOrgId, siteId }
      : standalone
        ? { orgId: resolvedOrgId }
        : { entId, orgId: resolvedOrgId };

    sendRequest(apiKey, devicesUuid, restfulParams, {
      type: "all",
      group: "surveillance",
    });
  };

  // Fetch a single view's detail (name, gridTemplate, slots) by its id
  const fetchViewDetail = useCallback(
    (viewId, intent = "fetch") => {
      if (!isAuthReady || !siteId || !viewId) return;

      const requestUuid = uuidv4();
      detailRequestMapRef.current[requestUuid] = { viewId, intent };

      const apiKey = standalone
        ? "/orgs/:orgId/sites/:siteId/settings/liveViews/:liveViewId.read"
        : "/ents/:entId/orgs/:orgId/sites/:siteId/settings/liveViews/:liveViewId.read";
      const restfulParams = standalone
        ? { orgId: resolvedOrgId, siteId, liveViewId: viewId }
        : { entId, orgId: resolvedOrgId, siteId, liveViewId: viewId };

      sendRequest(apiKey, requestUuid, restfulParams);
    },
    [isAuthReady, siteId, standalone, resolvedOrgId, entId, sendRequest]
  );

  const applyViewToWall = useCallback(
    (view, detail) => {
      const layoutKey =
        LAYOUT_TYPES[(detail.gridTemplate ?? 1) - 1]?.key ?? DEFAULT_LAYOUT;
      const isSingleView = layoutKey === "1x1";

      const slots = (detail.slots ?? []).map((slot) => ({
        slotIndex: slot.slotIndex,
        deviceId: slot.deviceId,
        siteId: slot.siteId || findDeviceById(slot.deviceId)?.siteId || "",
        name: cameraNameById[slot.deviceId] ?? slot.deviceId,
        linkStatus: findDeviceById(slot.deviceId)?.linkStatus ?? "offline",
        ...resolveStreamConfigForDevice(slot.deviceId),
        streamId: isSingleView ? 1 : 2,
      }));

      setActiveView({
        id: view.id,
        name: detail.name ?? view.name,
        layout: layoutKey,
        slots,
      });
    },
    [cameraNameById, findDeviceById, resolveStreamConfigForDevice]
  );

  useEffect(() => {
    fetchLiveViews();
    fetchDevices();
  }, [isAuthReady, auth.orgs?.id, auth.realm?.id, auth.sites?.id]);

  // Handle list response — once views are loaded, fetch each view's detail by id
  useEffect(() => {
    staleListUuidsRef.current.forEach((requestUuid) => {
      if (!(requestUuid in dataMap)) return;
      getData(requestUuid);
      staleListUuidsRef.current.delete(requestUuid);
    });

    const latestListUuid = latestListUuidRef.current;
    if (!latestListUuid || !(latestListUuid in dataMap)) return;
    const response = getData(latestListUuid);
    latestListUuidRef.current = null;
    if (response?.data) {
      setViews(response.data);
      response.data.forEach((view) => fetchViewDetail(view.id));
    }
  }, [dataMap]);

  // Handle site surveillance devices → sidebar Device (standalone ipcam) + NVR (with child cameras)
  useEffect(() => {
    const response = getData(devicesUuid);
    if (!response?.data) return;

    const items = Array.isArray(response.data) ? response.data : [];
    const { devices: standaloneCameras, nvrs: nvrList } = partitionSurveillanceDevices(items);

    setDevices(standaloneCameras);
    setNvrs(nvrList);
  }, [dataMap]);

  // Persist saved view metadata (sidebar + cache) without resetting the wall.
  const recordSavedView = useCallback((viewId, detail, { linkActiveView = false } = {}) => {
    if (!viewId || !detail) return;

    setViewDetailsMap((prev) => ({ ...prev, [viewId]: detail }));
    setViews((prev) => {
      const exists = prev.some((v) => v.id === viewId);
      if (exists) {
        return prev.map((v) =>
          v.id === viewId ? { ...v, name: detail.name ?? v.name } : v
        );
      }
      return [...prev, { id: viewId, name: detail.name ?? "View" }];
    });

    if (linkActiveView) {
      setActiveView((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          id: viewId,
          name: detail.name ?? prev.name,
        };
      });
    }
  }, []);

  // Handle view-detail / update responses
  useEffect(() => {
    Object.keys(detailRequestMapRef.current).forEach((requestUuid) => {
      if (!(requestUuid in dataMap)) return;

      const { viewId, intent, submittedDetail } = detailRequestMapRef.current[requestUuid];
      const response = getData(requestUuid);
      delete detailRequestMapRef.current[requestUuid];

      if (intent === "update") {
        const error = getResponseError(response);
        if (error) {
          settleSave(requestUuid, { ok: false, message: error });
          return;
        }
        recordSavedView(viewId, response?.data ?? submittedDetail, { linkActiveView: true });
        settleSave(requestUuid, { ok: true });
        return;
      }

      if (!response?.data) return;

      if (intent === "play") {
        setViewDetailsMap((prev) => ({ ...prev, [viewId]: response.data }));
        const view =
          views.find((v) => v.id === viewId) || { id: viewId, name: response.data.name };
        applyViewToWall(view, response.data);
        return;
      }

      setViewDetailsMap((prev) => ({ ...prev, [viewId]: response.data }));
    });
  }, [dataMap, views, applyViewToWall, recordSavedView, settleSave]);

  // Handle create responses — add view to sidebar without resetting the wall
  useEffect(() => {
    Object.keys(createRequestMapRef.current).forEach((requestUuid) => {
      if (!(requestUuid in dataMap)) return;

      const submitted = createRequestMapRef.current[requestUuid];
      const response = getData(requestUuid);
      delete createRequestMapRef.current[requestUuid];

      const error = getResponseError(response);
      if (error) {
        settleSave(requestUuid, { ok: false, message: error });
        return;
      }

      // The server answers with just `{ id, version }`; name/layout come from the submitted payload.
      const created = response?.data ?? response;
      const viewId = created?.id ?? created?.liveViewId;
      if (viewId) {
        recordSavedView(
          viewId,
          { ...submitted, ...created, id: viewId, name: created.name ?? submitted?.name },
          { linkActiveView: true }
        );
      }
      // The create response shape isn't guaranteed to carry the new id, so
      // re-read the list to make sure the view shows up either way.
      fetchLiveViews();
      settleSave(requestUuid, { ok: true });
    });
  }, [dataMap, recordSavedView, settleSave]);

  // Called when the user saves changes in EditViewModal
  const handleSaveView = (updatedView) => {
    setViews((prev) =>
      prev.map((v) => (v.id === updatedView.id ? updatedView : v))
    );
  };

  // Called when the user confirms deletion in EditViewModal → DeleteViewModal.
  // Removes the view locally and calls the delete API using its liveViewId.
  const handleDeleteView = useCallback(
    (id) => {
      if (!id) return;

      setViews((prev) => prev.filter((v) => v.id !== id));
      setViewDetailsMap((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setActiveView((prev) => (prev?.id === id ? null : prev));

      const requestUuid = uuidv4();
      const apiKey = standalone
        ? "/orgs/:orgId/sites/:siteId/settings/liveViews/:liveViewId.delete"
        : "/ents/:entId/orgs/:orgId/sites/:siteId/settings/liveViews/:liveViewId.delete";
      const restfulParams = standalone
        ? { orgId: resolvedOrgId, siteId, liveViewId: id }
        : { entId, orgId: resolvedOrgId, siteId, liveViewId: id };

      sendRequest(apiKey, requestUuid, restfulParams);
      return requestUuid;
    },
    [standalone, resolvedOrgId, siteId, entId, sendRequest]
  );

  // Called when the user presses ▶ on a ViewItem — sends the recorded view
  // detail (name / gridTemplate / slots) to the VideoWall play area.
  const handlePlayView = useCallback(
    (view) => {
      if (!view?.id) return;

      const cached = viewDetailsMap[view.id];
      if (cached) {
        applyViewToWall(view, cached);
      }

      fetchViewDetail(view.id, "play");
    },
    [viewDetailsMap, applyViewToWall, fetchViewDetail]
  );

  // Click sidebar camera → next empty grid slot; drag → target slot; section ▶ → fill in order.
  // isSingleView: true for 1x1 layout → use HD (streamId=1), false for multiview → use SD (streamId=2)
  const buildSlot = useCallback(
    (camera, slotIndex, isSingleView = false) => {
      const config = resolveStreamConfig({ device: camera, authToken });
      // Override streamId based on layout: 1x1 (single view) uses HD, others use SD
      return {
        slotIndex,
        deviceId: camera.id,
        siteId: camera.siteId || "",
        name: camera.name ?? camera.id,
        linkStatus: camera.linkStatus ?? "offline",
        ...config,
        streamId: isSingleView ? 1 : 2,
      };
    },
    [authToken]
  );

  const assignCameraToWall = useCallback(
    (camera, targetSlotIndex) => {
      if (!camera?.id) return;

      setActiveView((prev) => {
        const layoutKey = prev?.layout ?? DEFAULT_LAYOUT;
        const isSingleView = layoutKey === "1x1";
        const totalSlots = LAYOUT_TYPES.find((l) => l.key === layoutKey)?.total ?? 16;
        const existing = (prev?.slots ?? []).filter((s) => s.deviceId !== camera.id);

        let slotIndex = targetSlotIndex;
        if (slotIndex === undefined || slotIndex === null) {
          // Fill in view order from slot 0 — use the lowest empty index first
          const occupied = new Set(existing.map((s) => s.slotIndex));
          slotIndex = 0;
          for (let i = 0; i < totalSlots; i += 1) {
            if (!occupied.has(i)) {
              slotIndex = i;
              break;
            }
          }
        }

        const nextSlots = [
          ...existing.filter((s) => s.slotIndex !== slotIndex),
          buildSlot(camera, slotIndex, isSingleView),
        ];

        return {
          ...prev,
          id: prev?.id,
          name: prev?.name ?? "Live View",
          layout: layoutKey,
          slots: nextSlots,
        };
      });
    },
    [buildSlot]
  );

  const handlePlayCamera = useCallback(
    (camera, slotIndex) => {
      assignCameraToWall(camera, slotIndex);
    },
    [assignCameraToWall]
  );

  const handleMoveSlot = useCallback((fromSlotIndex, toSlotIndex) => {
    if (fromSlotIndex === toSlotIndex) return;

    setActiveView((prev) => {
      const slots = prev?.slots ?? [];
      const fromSlot = slots.find((s) => s.slotIndex === fromSlotIndex);
      if (!fromSlot) return prev;

      const toSlot = slots.find((s) => s.slotIndex === toSlotIndex);
      const rest = slots.filter(
        (s) => s.slotIndex !== fromSlotIndex && s.slotIndex !== toSlotIndex
      );

      const nextSlots = toSlot
        ? [
            ...rest,
            { ...fromSlot, slotIndex: toSlotIndex },
            { ...toSlot, slotIndex: fromSlotIndex },
          ]
        : [...rest, { ...fromSlot, slotIndex: toSlotIndex }];

      return {
        ...prev,
        id: prev?.id,
        name: prev?.name ?? "Live View",
        layout: prev?.layout ?? DEFAULT_LAYOUT,
        slots: nextSlots,
      };
    });
  }, []);

  const handleRemoveCamera = useCallback((slotIndex) => {
    setActiveView((prev) => {
      if (!prev) return prev;
      const slots = (prev?.slots ?? []).filter((s) => s.slotIndex !== slotIndex);
      return {
        ...prev,
        id: prev?.id,
        name: prev?.name ?? "Live View",
        layout: prev?.layout ?? DEFAULT_LAYOUT,
        slots,
      };
    });
  }, []);

  const handlePlayCameras = useCallback(
    (cameras) => {
      if (!cameras?.length) return;

      setActiveView((prev) => {
        const layoutKey = prev?.layout ?? DEFAULT_LAYOUT;
        const isSingleView = layoutKey === "1x1";
        const totalSlots = LAYOUT_TYPES.find((l) => l.key === layoutKey)?.total ?? 16;
        const slots = cameras
          .filter((c) => c?.id)
          .slice(0, totalSlots)
          .map((camera, index) => buildSlot(camera, index, isSingleView));

        return {
          ...prev,
          id: prev?.id,
          name: prev?.name ?? "Live View",
          layout: layoutKey,
          slots,
        };
      });
    },
    [buildSlot]
  );

  const handleLayoutChange = useCallback((layoutKey) => {
    setActiveView((prev) => {
      const isSingleView = layoutKey === "1x1";
      const updatedSlots = (prev?.slots ?? []).map((slot) => ({
        ...slot,
        streamId: isSingleView ? 1 : 2,
      }));

      return {
        id: prev?.id,
        name: prev?.name ?? "Live View",
        layout: layoutKey,
        slots: updatedSlots,
      };
    });
  }, []);

  const handleClearActiveView = useCallback(() => {
    setActiveView((prev) => (prev ? { ...prev, slots: [] } : prev));
  }, []);

  // Called from the Save View modal when "Existing View" is chosen — sends
  // the currently displayed layout/camera arrangement (gridTemplate + slots
  // with deviceId, in order) to overwrite that saved view.
  const handleUpdateView = useCallback(
    (viewId, updatedData) => {
      if (!viewId) return;

      const mergedDetail = {
        ...(viewDetailsMap[viewId] ?? {}),
        ...updatedData,
        id: viewId,
      };

      const requestUuid = uuidv4();
      detailRequestMapRef.current[requestUuid] = {
        viewId,
        intent: "update",
        submittedDetail: mergedDetail,
      };
      const result = trackSave(requestUuid);

      const apiKey = standalone
        ? "/orgs/:orgId/sites/:siteId/settings/liveViews/:liveViewId.update"
        : "/ents/:entId/orgs/:orgId/sites/:siteId/settings/liveViews/:liveViewId.update";
      const restfulParams = standalone
        ? { orgId: resolvedOrgId, siteId, liveViewId: viewId }
        : { entId, orgId: resolvedOrgId, siteId, liveViewId: viewId };

      sendRequest(apiKey, requestUuid, restfulParams, updatedData);
      return result;
    },
    [standalone, resolvedOrgId, siteId, entId, sendRequest, viewDetailsMap, trackSave]
  );

  // Create a new Liveview Configuration via WebSocket API.
  // Resolves { ok, message } once the server answers (or the request times out).
  const handleCreateView = (newViewData) => {
    const requestUuid = uuidv4();
    createRequestMapRef.current[requestUuid] = newViewData;
    const result = trackSave(requestUuid);
    if (!standalone) {
      sendRequest(
        "/ents/:entId/orgs/:orgId/sites/:siteId/settings/liveViews.create",
        requestUuid,
        { entId, orgId: resolvedOrgId, siteId },
        newViewData
      );
    } else {
      sendRequest(
        "/orgs/:orgId/sites/:siteId/settings/liveViews.create",
        requestUuid,
        { orgId: resolvedOrgId, siteId },
        newViewData
      );
    }
    return result;
  };

  // Toggle favorite via device settings.update (MSP vs standalone site paths)
  const handleToggleFavorite = useCallback(
    (deviceId) => {
      if (!deviceId || !isAuthReady || !siteId) return;

      const current =
        devices.find((d) => d.id === deviceId)?.favorited === true ||
        nvrs
          .flatMap((n) => n.cameras || [])
          .find((c) => c.id === deviceId)?.favorited === true;
      const nextFavorite = !current;

      // Optimistic UI — Favorite section + stars update immediately
      setDevices((prev) =>
        prev.map((d) => (d.id === deviceId ? { ...d, favorited: nextFavorite } : d))
      );
      setNvrs((prev) =>
        prev.map((nvr) => ({
          ...nvr,
          cameras: (nvr.cameras || []).map((c) =>
            c.id === deviceId ? { ...c, favorited: nextFavorite } : c
          ),
        }))
      );

      const apiKey = standalone
        ? "/orgs/:orgId/sites/:siteId/devices/:deviceId/settings.update"
        : "/ents/:entId/orgs/:orgId/sites/:siteId/devices/:deviceId/settings.update";
      const restfulParams = standalone
        ? { orgId: resolvedOrgId, siteId, deviceId }
        : { entId, orgId: resolvedOrgId, siteId, deviceId };

      sendRequest(apiKey, uuidv4(), restfulParams, {
        data: { info: { favorite: nextFavorite } },
      });
    },
    [
      isAuthReady,
      siteId,
      devices,
      nvrs,
      standalone,
      resolvedOrgId,
      entId,
      sendRequest,
    ]
  );

  const wallSlots = useMemo(
    () =>
      (activeView?.slots ?? []).map((slot) => ({
        ...slot,
        linkStatus:
          findDeviceById(slot.deviceId)?.linkStatus ?? slot.linkStatus ?? "offline",
      })),
    [activeView?.slots, findDeviceById]
  );

  return (
    <Box sx={{ flexGrow: 1, display: "flex", height: "100%" }}>
      <ResourceSidebar
        views={views}
        devices={devices}
        nvrs={nvrs}
        onEditView={setEditingView}
        onPlayView={handlePlayView}
        onPlayCamera={handlePlayCamera}
        onPlayCameras={handlePlayCameras}
        onToggleFavorite={handleToggleFavorite}
        onCreateView={handleCreateView}
      />
      <VideoWall
        activeView={activeView}
        views={views}
        viewDetailsMap={viewDetailsMap}
        onCreateView={handleCreateView}
        onUpdateView={handleUpdateView}
        onAssignCamera={handlePlayCamera}
        onMoveSlot={handleMoveSlot}
        onRemoveCamera={handleRemoveCamera}
        onRequestStartViewing={requestStartViewing}
        onLayoutChange={handleLayoutChange}
        onCloseView={handleClearActiveView}
        slots={wallSlots}
      />

      <EditViewModal
        open={!!editingView}
        view={editingView}
        onClose={() => setEditingView(null)}
        onSave={handleSaveView}
        onDelete={handleDeleteView}
      />
    </Box>
  );
};

export default SurveillanceLiveview;
